//
// # DummyDeviceDialog
//
// Dialog for adding dummy devices around selected layout instances.
// User specifies nrows (above/below), ncols (left/right), and instance names.
// Rendered as a portal overlay, styled like the Edit Parameters dialog.
//

import React, { useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { DummyDeviceDialogState } from "../../types";
import { useWorkspace } from "../../context";
import { extractErrorMessage } from "../../errorUtil";

export interface DummyDeviceDialogProps {
  readonly state: DummyDeviceDialogState;
  readonly onClose: () => void;
}

export const DummyDeviceDialog: React.FC<DummyDeviceDialogProps> = ({
  state,
  onClose,
}) => {
  const workspace = useWorkspace();
  const sourceName = state.instanceNames[0] ?? "instance";
  const [nrows, setNrows] = useState(String(state.nrows));
  const [ncols, setNcols] = useState(String(state.ncols));
  const [dummyName, setDummyName] = useState(`${sourceName}_dummy`);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const firstInputRef = useRef<HTMLInputElement>(null);

  // Focus first input on mount
  React.useEffect(() => {
    firstInputRef.current?.focus();
    firstInputRef.current?.select();
  }, []);

  const handleSubmit = useCallback(async () => {
    const nrowsNum = Number(nrows);
    const ncolsNum = Number(ncols);

    if (isNaN(nrowsNum) || nrowsNum < 0) {
      setError("Rows must be a non-negative number");
      return;
    }
    if (isNaN(ncolsNum) || ncolsNum < 0) {
      setError("Columns must be a non-negative number");
      return;
    }
    if (nrowsNum === 0 && ncolsNum === 0) {
      setError("At least one of rows or columns must be > 0");
      return;
    }
    if (!dummyName.trim()) {
      setError("Dummy name cannot be empty");
      return;
    }

    setError(null);
    setSubmitting(true);

    const result = await workspace.submitDummyDevices({
      designIdent: state.designIdent,
      instanceIds: state.instanceIds,
      nrows: nrowsNum,
      ncols: ncolsNum,
      dummyName: dummyName.trim(),
    });

    setSubmitting(false);

    if (result.err) {
      setError(extractErrorMessage(result.val));
    } else {
      onClose();
    }
  }, [nrows, ncols, dummyName, state, workspace, onClose]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "Enter") {
        e.preventDefault();
        void handleSubmit();
      }
    },
    [onClose, handleSubmit]
  );

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const instanceLabel =
    state.instanceNames.length === 1
      ? state.instanceNames[0]
      : `${state.instanceNames.length} instances`;

  return createPortal(
    <div
      className="command-palette-backdrop"
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div
        className="command-palette add-instance-dialog"
        role="dialog"
        aria-label="Add Dummy Devices"
      >
        <div className="command-palette-prompt-label">
          {instanceLabel} — Add Dummy Devices
        </div>

        <div className="add-instance-params">
          <div className="add-instance-param-field">
            <label className="add-instance-param-label">
              nrows
              <span className="add-instance-param-hint">
                {" "}
                — Number of dummy rows above AND below
              </span>
            </label>
            <input
              ref={firstInputRef}
              className="add-instance-param-input"
              type="number"
              min="0"
              value={nrows}
              onChange={(e) => setNrows(e.target.value)}
              disabled={submitting}
            />
            <span className="add-instance-param-constraint">
              min: 0, default: 1
            </span>
          </div>

          <div className="add-instance-param-field">
            <label className="add-instance-param-label">
              ncols
              <span className="add-instance-param-hint">
                {" "}
                — Number of dummy columns left AND right
              </span>
            </label>
            <input
              className="add-instance-param-input"
              type="number"
              min="0"
              value={ncols}
              onChange={(e) => setNcols(e.target.value)}
              disabled={submitting}
            />
            <span className="add-instance-param-constraint">
              min: 0, default: 1
            </span>
          </div>

          <div className="add-instance-param-field">
            <label className="add-instance-param-label">
              dummyName
              <span className="add-instance-param-hint">
                {" "}
                — Base name (suffixed with _above, _below, _left, _right)
              </span>
            </label>
            <input
              className="add-instance-param-input"
              type="text"
              value={dummyName}
              onChange={(e) => setDummyName(e.target.value)}
              disabled={submitting}
              autoComplete="off"
              spellCheck={false}
            />
          </div>
        </div>

        {(error || state.error) && (
          <div
            className="command-palette-empty"
            style={{ color: "var(--color-ide-error)" }}
          >
            {error ?? state.error}
          </div>
        )}

        <div className="add-instance-footer">
          <span className="add-instance-hint">
            {submitting
              ? "Adding dummy devices..."
              : "Enter to add, Escape to cancel"}
          </span>
          <button
            className="add-instance-btn"
            onClick={() => void handleSubmit()}
            disabled={submitting}
          >
            {submitting ? "Adding..." : "Add"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
